import { absolutePosition } from "./auto-fuse";
import { getDefinition } from "./catalog";
import { effectiveDimensions, effectiveTerminals } from "./effective";
import { pickOrderedAssignment } from "./ordered-assignment";
import { endpointPos } from "./store-helpers";
import type { Cable, SystemNode, Terminal, TerminalSide } from "./types";

export type CableEndpointUpdate = {
  fromTerminalId?: string;
  toTerminalId?: string;
};

/** All members must share one effective side — offset is what measures swap distance. */
type SwapGroup = { side: TerminalSide; terminals: Terminal[] };

/** Partitioned by effective side, so an override can't drag a cable across the body. */
function collectSwapGroups(
  def: ReturnType<typeof getDefinition>,
  node: Extract<SystemNode, { kind: "component" }>,
): SwapGroup[] {
  // A busbar stud is a physical connection the user may name or reserve.
  // Routing must not silently rewire it, even when another stud is electrically equivalent.
  if (!def || def.renderVariant === "busbar") return [];
  const groups: SwapGroup[] = [];
  const effective = effectiveTerminals(def, node);
  const explicit = def.interchangeableTerminalGroups;
  if (explicit && explicit.length > 0) {
    const byId = new Map(effective.map((t) => [t.id, t]));
    for (const ids of explicit) {
      const members = ids
        .map((id) => byId.get(id))
        .filter((t): t is Terminal => Boolean(t));
      if (members.length < 2) continue;
      const bySide = new Map<TerminalSide, Terminal[]>();
      for (const t of members) {
        const list = bySide.get(t.side) ?? [];
        list.push(t);
        bySide.set(t.side, list);
      }
      for (const [side, terminals] of bySide) {
        if (terminals.length >= 2) groups.push({ side, terminals });
      }
    }
  }
  return groups;
}

/** Reorder cables across interchangeable terminals to remove crossings. Cables outside
 *  `cableIds` keep their terminal, held out of the pool so two never land on one stud. */
export function planTerminalSwaps(
  nodes: SystemNode[],
  cables: Cable[],
  cableIds: string[],
): Map<string, CableEndpointUpdate> {
  const idSet = new Set(cableIds);
  const updates = new Map<string, CableEndpointUpdate>();

  for (const node of nodes) {
    if (node.kind !== "component") continue;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def) continue;
    const groups = collectSwapGroups(def, node);
    if (groups.length === 0) continue;

    const abs = absolutePosition(node, nodes);
    const dims = effectiveDimensions(def, node);

    for (const { side, terminals } of groups) {
      const isVertical = side === "left" || side === "right";
      const sideLen = isVertical ? dims.height : dims.width;

      type Touch = {
        cableId: string;
        end: "from" | "to";
        currentTerminalId: string;
        targetCoord: number;
      };
      const termIds = new Set(terminals.map((t) => t.id));
      const touches: Touch[] = [];
      for (const c of cables) {
        if (!idSet.has(c.id)) continue;
        let touch: Touch | null = null;
        if (c.fromNodeId === node.id && termIds.has(c.fromTerminalId)) {
          const farPt = endpointPos(c.toNodeId, c.toTerminalId, nodes);
          if (farPt) {
            touch = {
              cableId: c.id,
              end: "from",
              currentTerminalId: c.fromTerminalId,
              targetCoord: isVertical ? farPt.y : farPt.x,
            };
          }
        } else if (c.toNodeId === node.id && termIds.has(c.toTerminalId)) {
          const farPt = endpointPos(c.fromNodeId, c.fromTerminalId, nodes);
          if (farPt) {
            touch = {
              cableId: c.id,
              end: "to",
              currentTerminalId: c.toTerminalId,
              targetCoord: isVertical ? farPt.y : farPt.x,
            };
          }
        }
        if (touch) touches.push(touch);
      }
      if (touches.length < 2) continue;

      const lockedTermIds = new Set<string>();
      for (const c of cables) {
        if (idSet.has(c.id)) continue;
        if (c.fromNodeId === node.id && termIds.has(c.fromTerminalId)) {
          lockedTermIds.add(c.fromTerminalId);
        }
        if (c.toNodeId === node.id && termIds.has(c.toTerminalId)) {
          lockedTermIds.add(c.toTerminalId);
        }
      }
      const freeTerminals = terminals
        .filter((t) => !lockedTermIds.has(t.id))
        .sort((a, b) => a.offset - b.offset);
      if (freeTerminals.length < touches.length) continue;

      const sortedTouches = [...touches].sort(
        (a, b) => a.targetCoord - b.targetCoord,
      );
      const termCoord = (t: Terminal) =>
        isVertical
          ? abs.y + t.offset * sideLen
          : abs.x + t.offset * sideLen;
      const targets = sortedTouches.map((t) => t.targetCoord);
      const studs = freeTerminals.map(termCoord);

      const picked = pickOrderedAssignment(targets, studs);
      if (picked.length !== sortedTouches.length) continue;

      for (let i = 0; i < sortedTouches.length; i++) {
        const t = sortedTouches[i];
        const newTerm = freeTerminals[picked[i]];
        if (newTerm.id === t.currentTerminalId) continue;
        const cur = updates.get(t.cableId) ?? {};
        if (t.end === "from") cur.fromTerminalId = newTerm.id;
        else cur.toTerminalId = newTerm.id;
        updates.set(t.cableId, cur);
      }
    }
  }

  return updates;
}

export const planBusbarStudSwaps = planTerminalSwaps;
